/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types"
import { useEffect, useState, useCallback } from "react"

// @mui material components
import Link from "@mui/material/Link"

// Material Dashboard 2 React components
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"

import uenoLogo from "assets/images/logos/Brand.svg"
import { fetchImageBase64, IMAGE_TYPES } from "monitor/Configurator/service"
import { toImageDataUrl, getCachedImage, setCachedImage } from "utils/remoteImageCache"

const FOOTER_LOGO_CACHE_KEY = "app.footerLogo.dataUrl"
const FOOTER_LOGO_CACHE_TS_KEY = "app.footerLogo.ts"
const FOOTER_LOGO_CACHE_TTL_MS = 1000 * 60 * 30 // 30 min

function Footer({ company, links }) {
  const [footerLogo, setFooterLogo] = useState(() => {
    const cached = getCachedImage({
      key: FOOTER_LOGO_CACHE_KEY,
      tsKey: FOOTER_LOGO_CACHE_TS_KEY,
      ttlMs: FOOTER_LOGO_CACHE_TTL_MS,
    })
    return cached || uenoLogo
  })

  const fetchAndCacheFooterLogo = useCallback(async () => {
    try {
      const companyId = (sessionStorage.getItem("companyId") || localStorage.getItem("companyId") || "").trim()
      if (!companyId) {
        setFooterLogo(uenoLogo)
        return
      }
      const payload = await fetchImageBase64(IMAGE_TYPES.FOOTER, companyId)
      const nextLogo = toImageDataUrl(payload)

      setCachedImage({
        key: FOOTER_LOGO_CACHE_KEY,
        tsKey: FOOTER_LOGO_CACHE_TS_KEY,
        value: nextLogo,
      })
      setFooterLogo(nextLogo || uenoLogo)
    } catch {
      setFooterLogo(uenoLogo)
    }
  }, [])

  useEffect(() => {
    const cached = getCachedImage({
      key: FOOTER_LOGO_CACHE_KEY,
      tsKey: FOOTER_LOGO_CACHE_TS_KEY,
      ttlMs: FOOTER_LOGO_CACHE_TTL_MS,
    })

    if (cached !== undefined) {
      setFooterLogo(cached || uenoLogo)
      return
    }

    fetchAndCacheFooterLogo()
  }, [fetchAndCacheFooterLogo])
  //Test
  useEffect(() => {
    const handleFooterRefresh = () => {
      fetchAndCacheFooterLogo()
    }

    window.addEventListener("footer-logo:refresh", handleFooterRefresh)
    return () => {
      window.removeEventListener("footer-logo:refresh", handleFooterRefresh)
    }
  }, [fetchAndCacheFooterLogo])

  const renderLinks = () =>
    links.map((link) => (
      <MDBox key={link.name} component="li" px={2} lineHeight={1}>
        <Link href={link.href} target="_blank">
          <MDTypography variant="button" fontWeight="regular" color="text">
            <MDBox component="img" src={footerLogo} alt="brand-logo" mt={1} />
          </MDTypography>
        </Link>
      </MDBox>
    ))

  return (
    <MDBox
      width="100%"
      display="flex"
      flexDirection={{ xs: "column", lg: "row" }}
      justifyContent="space-between"
      alignItems="center"
      px={1.5}
    >
      <MDBox
        component="ul"
        sx={({ breakpoints }) => ({
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          listStyle: "none",
          mt: 3,
          mb: 0,
          p: 0,
          [breakpoints.up("lg")]: {
            mt: 0,
          },
        })}
      >
        {renderLinks()}
      </MDBox>
    </MDBox>
  )
}

// Setting default values for the props of Footer
Footer.defaultProps = {
  company: {},
  links: [{ href: "#", name: "Links", logo: { uenoLogo } }],
}

// Typechecking props for the Footer
Footer.propTypes = {
  company: PropTypes.objectOf(PropTypes.string),
  links: PropTypes.arrayOf(PropTypes.object),
}

export default Footer
